/**
 * 通用缓存 Store
 * 适用于任何 Vue3 + Pinia 项目的缓存管理
 */

import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { cacheRules, cacheTimeConfig } from './config'

// ==================== 类型定义 ====================

export interface CacheItem<T = unknown> {
    data: T
    timestamp: number
    expiry: number
    key: string
}

export interface RequestState {
    loading: boolean
    error: string | null
    lastRequest?: number
}

// 泛型接口，支持项目自定义时间配置
export interface CacheTimeConfig {
    [key: string]: number
    // 配置项名 -> 缓存时间（毫秒）
}

export const useCacheStore = defineStore('cache', () => {
    // ==================== 状态管理 ====================

    // 缓存存储
    const cache = ref<Map<string, CacheItem>>(new Map())

    // 请求状态管理
    const requestStates = ref<Map<string, RequestState>>(new Map())

    // ==================== 计算属性 ====================

    // 缓存统计信息
    const cacheStats = computed(() => ({
        totalItems: cache.value.size,
        activeRequests: Array.from(requestStates.value.values()).filter(s => s.loading).length,
        memoryUsage: JSON.stringify(Array.from(cache.value.values())).length
    }))

    // ==================== 核心缓存方法 ====================

    /**
     * 设置缓存项
     */
    const setCache = <T>(key: string, data: T, expiry: number): void => {
        const cacheItem: CacheItem<T> = {
            data,
            timestamp: Date.now(),
            expiry,
            key
        }
        cache.value.set(key, cacheItem)
    }

    /**
     * 获取缓存项
     */
    const getCache = <T>(key: string): T | null => {
        const item = cache.value.get(key)
        if (!item) return null

        // 检查是否过期
        if (Date.now() > item.timestamp + item.expiry) {
            cache.value.delete(key)
            return null
        }

        return item.data as T
    }

    /**
     * 检查缓存是否有效
     */
    const isCacheValid = (key: string): boolean => {
        const item = cache.value.get(key)
        if (!item) return false
        return Date.now() <= item.timestamp + item.expiry
    }

    /**
     * 删除缓存项
     */
    const removeCache = (key: string): boolean => {
        return cache.value.delete(key)
    }

    /**
     * 批量删除缓存（支持前缀匹配）
     */
    const removeCacheByPrefix = (prefix: string): number => {
        let count = 0
        const keysToDelete: string[] = []

        cache.value.forEach((_, key) => {
            if (key.startsWith(prefix)) {
                keysToDelete.push(key)
                count++
            }
        })

        keysToDelete.forEach(key => cache.value.delete(key))
        return count
    }

    /**
     * 清空所有缓存
     */
    const clearAllCache = (): void => {
        cache.value.clear()
        requestStates.value.clear()
    }

    /**
     * 清理过期缓存
     */
    const cleanExpiredCache = (): number => {
        let count = 0
        const now = Date.now()
        const keysToDelete: string[] = []

        cache.value.forEach((item, key) => {
            if (now > item.timestamp + item.expiry) {
                keysToDelete.push(key)
                count++
            }
        })

        keysToDelete.forEach(key => cache.value.delete(key))
        return count
    }

    // ==================== 请求状态管理 ====================

    /**
     * 设置请求状态
     */
    const setRequestState = (key: string, state: Partial<RequestState>): void => {
        const currentState = requestStates.value.get(key) || { loading: false, error: null }
        requestStates.value.set(key, { ...currentState, ...state })
    }

    /**
     * 获取请求状态
     */
    const getRequestState = (key: string): RequestState | null => {
        return requestStates.value.get(key) || null
    }

    /**
     * 检查是否正在加载
     */
    const isLoading = (key: string): boolean => {
        const state = requestStates.value.get(key)
        return state?.loading || false
    }

    /**
     * 防重复请求检查
     */
    const shouldSkipRequest = (key: string, debounceTime = 1000): boolean => {
        const state = requestStates.value.get(key)
        if (!state) return false

        // 如果正在加载，跳过
        if (state.loading) return true

        // 如果上次请求时间太近，跳过
        if (state.lastRequest && Date.now() - state.lastRequest < debounceTime) {
            return true
        }

        return false
    }

    // ==================== 高级缓存功能 ====================

    /**
     * 缓存装饰器 - 自动缓存异步函数结果
     * @param key 缓存键
     * @param fetcher 数据获取函数
     * @param expiry 缓存过期时间（毫秒）
     * @param forceRefresh 强制刷新
     */
    const withCache = <T>(
        key: string,
        fetcher: () => Promise<T>,
        expiry: number,
        forceRefresh = false
    ): Promise<T> => {
        return new Promise(async (resolve, reject) => {
            try {
                // 防重复请求
                if (!forceRefresh && shouldSkipRequest(key)) {
                    const cached = getCache<T>(key)
                    if (cached) {
                        resolve(cached)
                        return
                    }
                }

                // 检查缓存
                if (!forceRefresh) {
                    const cached = getCache<T>(key)
                    if (cached !== null) {
                        resolve(cached)
                        return
                    }
                }

                // 设置加载状态
                setRequestState(key, { loading: true, error: null, lastRequest: Date.now() })

                // 执行请求
                const result = await fetcher()

                // 缓存结果
                setCache(key, result, expiry)

                // 更新请求状态
                setRequestState(key, { loading: false, error: null })

                resolve(result)
            } catch (error) {
                // 更新错误状态
                setRequestState(key, {
                    loading: false,
                    error: error instanceof Error ? error.message : '请求失败'
                })
                reject(error)
            }
        })
    }

    // ==================== 倒计时功能 ====================

    /**
     * 开始倒计时
     * @param key 倒计时键
     * @param duration 倒计时时长（秒）
     */
    const startCountdown = (key: string, duration: number): void => {
        const countdownKey = `countdown:${key}`
        const startTime = Date.now()
        setCache(countdownKey, startTime, duration * 1000)
    }

    /**
     * 获取倒计时剩余时间
     * @param key 倒计时键
     * @returns 剩余秒数，0表示倒计时结束
     */
    const getCountdown = (key: string): number => {
        const countdownKey = `countdown:${key}`
        const startTime = getCache<number>(countdownKey)

        if (!startTime) return 0

        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        const cacheItem = cache.value.get(countdownKey)
        if (!cacheItem) return 0

        const totalDuration = Math.floor(cacheItem.expiry / 1000)
        const remaining = Math.max(0, totalDuration - elapsed)

        return remaining
    }

    /**
     * 检查是否正在倒计时
     * @param key 倒计时键
     */
    const isCountingDown = (key: string): boolean => {
        return getCountdown(key) > 0
    }

    /**
     * 停止倒计时
     * @param key 倒计时键
     */
    const stopCountdown = (key: string): void => {
        const countdownKey = `countdown:${key}`
        removeCache(countdownKey)
    }

    /**
     * 获取倒计时状态信息
     * @param key 倒计时键
     */
    const getCountdownInfo = (key: string): {
        isActive: boolean
        remaining: number
        total: number
    } => {
        const countdownKey = `countdown:${key}`
        const startTime = getCache<number>(countdownKey)
        const cacheItem = cache.value.get(countdownKey)

        if (!startTime || !cacheItem) {
            return { isActive: false, remaining: 0, total: 0 }
        }

        const totalDuration = Math.floor(cacheItem.expiry / 1000)
        const elapsed = Math.floor((Date.now() - startTime) / 1000)
        const remaining = Math.max(0, totalDuration - elapsed)

        return {
            isActive: remaining > 0,
            remaining,
            total: totalDuration
        }
    }

    // ==================== 智能缓存工具 ====================

    /**
     * 检查缓存规则
     * @param key 缓存键
     * @returns 缓存时间（毫秒）或 false（不缓存）或 'debounce'（防重复）
     */
    const getCacheRule = (key: string): number | false | 'debounce' => {
        // 精确匹配
        if (key in cacheRules) {
            return cacheRules[key as keyof typeof cacheRules]
        }

        // 前缀匹配（如 'config:' 匹配 'config:ranks'）
        for (const [ruleKey, ruleValue] of Object.entries(cacheRules)) {
            if (ruleKey.endsWith(':') && key.startsWith(ruleKey)) {
                return ruleValue
            }
        }

        // 默认不缓存
        return false
    }

    /**
     * 智能缓存 - 自动根据规则决定缓存策略
     * 这是最常用的方法，推荐优先使用
     */
    const smartCache = async <T>(
        key: string,
        fetcher: () => Promise<T>,
        forceRefresh = false
    ): Promise<T> => {
        const rule = getCacheRule(key)

        if (rule === false) {
            // 不缓存，直接请求
            return fetcher()
        } else if (rule === 'debounce') {
            // 只防重复，1秒缓存
            return withCache(key, fetcher, cacheTimeConfig.DEBOUNCE, forceRefresh)
        } else {
            // 正常缓存
            return withCache(key, fetcher, rule, forceRefresh)
        }
    }

    /**
     * 获取配置的缓存时间
     * @param timeKey 时间配置键
     */
    const getCacheTime = (timeKey: keyof typeof cacheTimeConfig): number => {
        return cacheTimeConfig[timeKey]
    }

    /**
     * 生成详情页缓存键
     * @param prefix 前缀
     * @param id ID
     */
    const getDetailKey = (prefix: string, id: number | string): string => {
        return `${prefix}:detail:${id}`
    }

    /**
     * 清除指定前缀的缓存项（兼容性方法）
     * @param prefix 前缀
     * @returns 删除的项目数量
     */
    const clearCacheByPrefix = (prefix: string): number => {
        return removeCacheByPrefix(prefix)
    }

    // ==================== 持久化存储 ====================

    /**
     * 保存指定缓存到localStorage
     * @param keys 要持久化的缓存键数组
     * @param storageKey localStorage的键名，默认为 'app_cache'
     */
    const persistCache = (keys: string[], storageKey = 'app_cache'): void => {
        const persistData: Record<string, CacheItem> = {}

        keys.forEach(key => {
            // 支持前缀匹配：如果 key 以 ':' 结尾，则持久化所有以该前缀开头的项
            if (key.endsWith(':')) {
                cache.value.forEach((item, k) => {
                    if (k.startsWith(key) && isCacheValid(k)) {
                        persistData[k] = item
                    }
                })
            } else {
                const item = cache.value.get(key)
                if (item && isCacheValid(key)) {
                    persistData[key] = item
                }
            }
        })

        localStorage.setItem(storageKey, JSON.stringify(persistData))
    }

    /**
     * 从localStorage恢复缓存
     * @param storageKey localStorage的键名，默认为 'app_cache'
     * @param fallbackKeys 回退的localStorage键名数组，用于版本兼容
     */
    const restoreCache = (storageKey = 'app_cache', fallbackKeys: string[] = []): void => {
        try {
            // 尝试恢复主键名的缓存
            let stored = localStorage.getItem(storageKey)

            // 如果主键名没有数据，尝试回退键名
            if (!stored && fallbackKeys.length > 0) {
                for (const fallbackKey of fallbackKeys) {
                    stored = localStorage.getItem(fallbackKey)
                    if (stored) break
                }
            }

            if (stored) {
                const persistData = JSON.parse(stored) as Record<string, CacheItem>

                Object.entries(persistData).forEach(([key, item]) => {
                    // 只恢复未过期的数据
                    if (Date.now() <= item.timestamp + item.expiry) {
                        cache.value.set(key, item)
                    }
                })
            }
        } catch (error) {
            console.warn('恢复缓存失败:', error)
        }
    }

    // ==================== 初始化和清理 ====================

    /**
     * 初始化缓存系统
     * @param options 初始化选项
     */
    const init = (options: {
        cleanupInterval?: number
        storageKey?: string
        fallbackStorageKeys?: string[]
        persistKeys?: string[]
    } = {}): void => {
        // 使用config.ts中的默认配置，避免重复定义
        const {
            cleanupInterval = 5 * 60 * 1000, // 从 cacheInitConfig 导入
            storageKey = 'game_rental_cache_v3', // 与config.ts保持一致
            fallbackStorageKeys = ['game_rental_cache_v2', 'app_cache'], // 与config.ts保持一致
            persistKeys = []
        } = options

        // 恢复持久化缓存
        if (persistKeys.length > 0) {
            restoreCache(storageKey, fallbackStorageKeys)
        }

        // 定期清理过期缓存
        if (cleanupInterval > 0) {
            setInterval(() => {
                cleanExpiredCache()
            }, cleanupInterval)
        }

        // 页面卸载时保存重要缓存
        if (typeof window !== 'undefined' && persistKeys.length > 0) {
            window.addEventListener('beforeunload', () => {
                persistCache(persistKeys, storageKey)
            })
        }
    }

    // ==================== 返回API ====================

    return {
        // 状态
        cache: computed(() => cache.value),
        cacheStats,

        // 基础缓存操作
        setCache,
        getCache,
        removeCache,
        clearAllCache,
        isCacheValid,
        cleanExpiredCache,
        removeCacheByPrefix,

        // 请求状态管理
        setRequestState,
        getRequestState,
        isLoading,
        shouldSkipRequest,

        // 高级功能
        withCache,
        smartCache,  // 🌟 主要使用这个

        // 倒计时功能
        startCountdown,
        getCountdown,
        isCountingDown,
        stopCountdown,
        getCountdownInfo,

        // 工具方法
        getCacheRule,
        getCacheTime,
        getDetailKey,
        clearCacheByPrefix, // 兼容性方法

        // 持久化
        persistCache,
        restoreCache,

        // 初始化
        init
    }
}) 